import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'
import { HiExclamation } from 'react-icons/hi'

import { AlertDialogBody, AlertDialogFooter, Box, Flex } from '@chakra-ui/react'

import {
  Alert,
  Box as DarkBox,
  Modal as DarkModal,
  Text as DarkText,
} from '@hivewatch/dark'

import { BasicAlertDialog, Button } from '@/components/ui'

interface ConfirmEmergencyCallDialogIProps {
  emergencyNumber: string
  isOpen: boolean
  onClose: () => void
  handleOnClick: () => void
}

export const ConfirmEmergencyCallDialog = ({
  emergencyNumber,
  isOpen,
  onClose,
  handleOnClick,
}: ConfirmEmergencyCallDialogIProps) => {
  const { t } = useTranslation()
  const { shouldEnableIncidentEmergencyCall } = usePermissions()
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const colorScheme = useLightMode()

  if (isRebrand) {
    return (
      <DarkModal
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveDisabled={!shouldEnableIncidentEmergencyCall}
        onClose={onClose}
        onSave={handleOnClick}
        saveButtonLabel={t('emergency_call:emergency_call')}
        showCancelButton
        size='compact'
        title={t('emergency_call:dialing_local_law_enforcement')}
        zIndex={1500}
      >
        <DarkText
          color={
            colorScheme === 'light' ? 'light.text.primary' : 'text.primary'
          }
          textStyle='body'
        >
          {t('emergency_call:emergency_call_body')}
        </DarkText>
        <DarkText
          color={
            colorScheme === 'light' ? 'light.text.primary' : 'text.primary'
          }
          mt='12px'
          textStyle='body-bold'
        >
          {emergencyNumber}
        </DarkText>
        <DarkBox mt='16px'>
          <Alert
            colorScheme={colorScheme}
            description={t('emergency_call:this_will_escalate')}
            title={t('common:warning_title')}
            variant='error'
          />
        </DarkBox>
      </DarkModal>
    )
  }

  return (
    <BasicAlertDialog
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      title='Dialing Local Law Enforcement'
    >
      <AlertDialogBody>
        <Box letterSpacing='-0.53px'>
          This will place an emergency call to the incident's local emergency
          number:
        </Box>
        <Box as='span' fontWeight='bold'>
          {emergencyNumber}
        </Box>
        <Flex alignItems='center' color='#D11D3A' flexDirection='row' pt='4'>
          <HiExclamation size='24px' />
          <Box
            fontSize='16px'
            fontWeight='medium'
            letterSpacing='-0.53px'
            ml='1'
          >
            <Box as='span' fontWeight='bold'>
              Warning:
            </Box>{' '}
            This will escalate the incident!
          </Box>
        </Flex>
      </AlertDialogBody>
      <AlertDialogFooter>
        <Button mr='3' onClick={onClose} variant='secondary'>
          Cancel
        </Button>
        <Button
          isDisabled={!shouldEnableIncidentEmergencyCall}
          onClick={handleOnClick}
          variant='primary'
        >
          Emergency Call
        </Button>
      </AlertDialogFooter>
    </BasicAlertDialog>
  )
}
